
import Vue from 'vue';
import RunnitsLogoMarkSVG from '@/assets/RunnitsLogoMarkSVG.vue';
import LoadingState from "@/components/states/LoadingState/LoadingState.vue";
import { BoardsV2AccessMixin, RunnitsCRUDMixin, TeamUserMixin } from '@/mixins';
import { RunnitsOwnerSelection } from '@/store';
import { ROUTER } from '@/router/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { KitsMixin } from '@/mixins/KitsMixin';
import { RUNNIT_TYPE, RunnitNodeDef, Team, User } from '@run-diffusion/shared';
import RunnitAccountSelectionDialog from '@/views/Runnits/RunnitAccountSelection/RunnitAccountSelectionDialog.vue';
import RunnitSideNavAccountCard from '@/views/Runnits/RunnitSideNav/RunnitSideNavAccountCard.vue';
import ComplexBackgroundSideNav from '@/views/Runnits/RunnitSideNav/ComplexBackgroundSideNav.vue';
import RunDiffusionWithRunnitMarkLogo from '@/assets/RunDiffusionWithRunnitMarkLogo.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { setPlatformHomePreference } from '@/utils';
import { lastBoardsVersion } from '@/utils/boardsVersion';
import AccountMenuBtn from '@/components/Toolbar/AccountMenuBtn.vue';
import { isRouteRunnitSideNavPage } from '@/router/utils';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import {
	getRunnitsSideNavGenerateExpanded,
	getRunnitsSideNavResourcesExpanded,
	setRunnitsSideNavGenerateExpanded,
	setRunnitsSideNavResourcesExpanded,
} from '@/utils/localStorage';
import {
	getDomainClaimDialogContent,
	DomainClaimDialogContent,
} from '@/utils/domainClaimRestriction';
import DomainClaimResolutionContent from '@/views/Runnits/RunnitSideNav/DomainClaimResolutionContent.vue';

export default Vue.extend({
	name: 'RunnitSideNav',
	mixins: [
		RunnitsCRUDMixin,
		TeamUserMixin,
		KitsMixin,
		BoardsV2AccessMixin,
	],
	data () {
		return {
			ROUTER,
			RUNNITS_OWNER_SELECTION,
			isResourcesExpanded: getRunnitsSideNavResourcesExpanded(),
			isGenerateExpanded: getRunnitsSideNavGenerateExpanded(),
			isGenerateFlyoutOpen: false,
			showDomainClaimPlanFlyoutMenu: false,
			domainClaimPlanFlyoutSuppressed: false,
			domainClaimFlyoutDismissTimer: null as ReturnType<typeof setTimeout> | null,
		};
	},

	computed: {
		...mapState([
			'isAdminProxyingUser',
			'runnitState',
			'toolbar',
			'user',
			'currSessions',
			'team',
		]),
		// App.vue opens Search on Cmd+K or Ctrl+K; show the one this keyboard has.
		searchShortcutHint (): string {
			return /mac/i.test(navigator.platform) ? '⌘K' : 'Ctrl+K';
		},
		...mapGetters([
			'displayLabels',
			'showStripeUserMembershipPortalBtn',
			'activeDomainClaimRestriction',
		]),
		...mapGetters('modelUpload', [
			'getUploading',
		]),
		// The Generate menu is the published Kits, in their stored order, with
		// their stored icon and name. Unpublishing a Kit or scoping it away from
		// web removes its nav item instead of leaving a dead one.
		navKits () {
			return (this.navigableKits || []).map((kit) => ({
				id: kit.id,
				label: kit.label,
				icon: kit.icon,
			}));
		},
		isSubscriptionsRoute () {
			return this.$route.name === ROUTER.RUNNITS_SUBSCRIPTIONS;
		},
		isHomeRoute () {
			return this.$route.name === ROUTER.RUNNITS_HOME;
		},
		isModelsRoute () {
			return this.$route.name === ROUTER.RUNNITS_MODELS_LIST;
		},
		isAllToolsRoute () {
			return this.$route.name === ROUTER.RUNNITS_ALL_TOOLS;
		},
		isRunnitBoardsPageRoute () {
			return this.$route.name === ROUTER.RUNNIT_BOARDS_PAGE || this.$route.name === ROUTER.RUNNIT_BOARDS_V2_PAGE;
		},
		isRunnitTemplatesPageRoute () {
			return this.$route.name === ROUTER.RUNNIT_BOARD_TEMPLATES_PAGE;
		},
		isLibraryRoute () {
			return this.$route.name === ROUTER.RUNNITS_LIBRARY;
		},
		isAppsRoute () {
			return this.$route.name === ROUTER.RUNNITS_APP_LIBRARY;
		},
		isHelpRoute () {
			return this.$route.name === ROUTER.HELP_CENTER;
		},
		isWhatsNewRoute () {
			return this.$route.name === ROUTER.RUNNITS_WHATS_NEW;
		},
		isReferralsRoute () {
			return this.$route.name === ROUTER.RUNNITS_REFERRALS;
		},
		isRunnitBoardRoute () {
			return this.$route.name === ROUTER.RUNNITS_BOARD;
		},
		isOpenSourceAppsRoute () {
			return [
				ROUTER.SESSIONS,
				ROUTER.SETUP_SESSION,
				ROUTER.ANOTHER_SESSION,
				ROUTER.SETUP_ANOTHER_SESSION,
				ROUTER.LAUNCH,
			].includes(this.$route.name);
		},
		isPluginsRoute () {
			return this.$route.name === ROUTER.PLUGINS;
		},
		shouldShowDomainClaimPlanSideNavFlyout (): boolean {
			return !!this.activeDomainClaimRestriction;
		},
		isScimProvisionedUser (): boolean {
			return Object.keys(this.user?.scim || {}).length > 0;
		},
		// Branched content for the Domain Claim Resolution Dialog (headline/body + CTAs).
		domainClaimDialogContent (): DomainClaimDialogContent | null {
			return getDomainClaimDialogContent({
				restriction: this.activeDomainClaimRestriction,
				teamDisplayLabel: this.displayLabels.team,
				ownerSelection: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM ? 'TEAM' : 'USER',
				isPaid: this.showStripeUserMembershipPortalBtn,
				isScim: this.isScimProvisionedUser,
			});
		},
		shouldAutoOpenDomainClaimPlanFlyout (): boolean {
			return !!(
				this.shouldShowDomainClaimPlanSideNavFlyout &&
				this.$vuetify.breakpoint.mdAndUp &&
				!this.domainClaimPlanFlyoutSuppressed
			);
		},
	},
	watch: {
		user: {
			immediate: true,
			handler (newVal: User, oldVal: User) {
				const oldUserId: string = oldVal?.id;
				const newUserId: string = newVal?.id;
				if (oldUserId && newUserId !== oldUserId) {
					this.rearmDomainClaimPlanFlyoutAutoOpen();
				}
			},
		},
		'runnitState.runnitsOwnerSelection': {
			immediate: true,
			handler (newVal: RunnitsOwnerSelection, oldVal: RunnitsOwnerSelection) {
				if (oldVal && newVal !== oldVal) {
					this.rearmDomainClaimPlanFlyoutAutoOpen();
				}
			}
		},
		team: {
			immediate: true,
			handler (newVal: Team, oldVal: Team) {
				const oldTeamId: string = oldVal?.id;
				const newTeamId: string = newVal?.id;
				if (oldTeamId && newTeamId !== oldTeamId) {
					this.rearmDomainClaimPlanFlyoutAutoOpen();
				}
			}
		},
		shouldAutoOpenDomainClaimPlanFlyout: {
			immediate: true,
			handler (open: boolean) {
				if (!open) {
					this.showDomainClaimPlanFlyoutMenu = false;
					return;
				}
				this.$nextTick(() => {
					this.showDomainClaimPlanFlyoutMenu = true;
				});
			},
		},
		'$store.state.triggers.screenResizeBreakpointTrigger': {
			immediate: true,
			handler (newVal, oldVal) {
				const oldSmAndDown: boolean = !!this._get(oldVal, 'smAndDown');
				const newSmAndDown: boolean = !!this._get(newVal, 'smAndDown');
				if (newSmAndDown !== oldSmAndDown) {
					const runnitsSideNavOpen: boolean = !!(!newSmAndDown && isRouteRunnitSideNavPage(this.$route, this.user));
					if (runnitsSideNavOpen !== this.toolbar.runnitsSideNavOpen) {
						this.updateToolbar({
							runnitsSideNavOpen,
						});
					}
				}
			},
		},
		// Collapse nested groups whenever the side nav collapses or expands
		'toolbar.runnitsSideNavCollapsed' (collapsed: boolean) {
			if (!collapsed) {
				// expanding → only close flyouts, preserve expansion state
				this.isGenerateFlyoutOpen = false;
			}
			// collapsing → do not force-close the flyout; it will be controlled by user click/outside click
		},
		'$route.name' (newVal: string, oldVal: string) {
			if (newVal !== oldVal) {
				// Only close flyouts on route change, preserve expansion state
				this.isGenerateFlyoutOpen = false;
			}
		},
		// Save expansion state changes to localStorage
		isResourcesExpanded (newVal: boolean) {
			setRunnitsSideNavResourcesExpanded(newVal);
		},
		isGenerateExpanded (newVal: boolean) {
			setRunnitsSideNavGenerateExpanded(newVal);
		},
	},
	methods: {
		onGenerateClick () {
			if (this.toolbar.runnitsSideNavCollapsed) {
				this.isGenerateFlyoutOpen = !this.isGenerateFlyoutOpen;
			} else {
				this.isGenerateExpanded = !this.isGenerateExpanded;
			}
		},
		setGenerateFlyoutOpen (open: boolean) {
			this.isGenerateFlyoutOpen = open;
		},
		...mapActions([
			'updateToolbar',
			'setShowMqlCalendarDialog',
			'updateRunnitState',
		]),
		openGlobalSearch () {
			this.updateRunnitState({
				globalSearchOpen: true,
				globalSearchTrigger: 'button',
			});
		},
		openSalesCalendar () {
			this.setShowMqlCalendarDialog(true);
		},
		toggleIsResourcesExpanded () {
			this.isResourcesExpanded = !this.isResourcesExpanded;
			// Watcher will automatically save to localStorage
		},
		goTo (routeName: string) {
			this.routerPush(this.$route, this.$router, { name: routeName });
		},
		onManagePlanClick () {
			// Straight navigation: Subscriptions for personal plans, Company Dashboard for
			// teams/enterprise. (The tokens & usage dialog opens from the token pill instead.)
			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				this.goTo(ROUTER.COMPANY_DASHBOARD);
			} else {
				this.goTo(ROUTER.RUNNITS_SUBSCRIPTIONS);
			}
		},
		rearmDomainClaimPlanFlyoutAutoOpen () {
			this.clearDomainClaimFlyoutDismissTimer();
			this.domainClaimPlanFlyoutSuppressed = false;
			if (!(this.$vuetify.breakpoint.mdAndUp && this.shouldShowDomainClaimPlanSideNavFlyout)) {
				return;
			}
			// Force retrigger so selection/team switches can re-open even if condition stayed true.
			this.showDomainClaimPlanFlyoutMenu = false;
			this.$nextTick(() => {
				this.showDomainClaimPlanFlyoutMenu = true;
			});
		},
		clearDomainClaimFlyoutDismissTimer () {
			if (this.domainClaimFlyoutDismissTimer) {
				clearTimeout(this.domainClaimFlyoutDismissTimer);
				this.domainClaimFlyoutDismissTimer = null;
			}
		},
		onDismissDomainClaimPlanFlyout (close: () => void) {
			this.clearDomainClaimFlyoutDismissTimer();
			this.domainClaimPlanFlyoutSuppressed = true;
			this.showDomainClaimPlanFlyoutMenu = false;
			close();

			// Re-open flyout every 10 minutes
			const DOMAIN_CLAIM_PLAN_FLYOUT_DISMISS_MS: number = 10 * 60 * 1000;
			this.domainClaimFlyoutDismissTimer = setTimeout(() => {
				this.domainClaimFlyoutDismissTimer = null;
				this.domainClaimPlanFlyoutSuppressed = false;
			}, DOMAIN_CLAIM_PLAN_FLYOUT_DISMISS_MS);
		},
		onDomainClaimContentClose (close: () => void) {
			this.showDomainClaimPlanFlyoutMenu = false;
			close();
		},
		goToOpenSourceApps () {
			setPlatformHomePreference('RD');
			if (this.toolbar.session) {
				this.routerPush(this.$route, this.$router, { name: ROUTER.LAUNCH, params: { sessionId: this.toolbar.session.id } });
			} else {
				this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
			}
		},
		openWhatsNew () {
			this.goTo(ROUTER.RUNNITS_WHATS_NEW);
		},
		closeSideNav () {
			this.updateToolbar({
				runnitsSideNavOpen: false,
			});
		},
		onBoardsNavClick () {
			// The boards list used last: Boards v2's, if this account has it, or the classic one
			const v2: boolean = this.boardsV2Access === 'on' && lastBoardsVersion() === 'v2';
			this.goTo(v2 ? ROUTER.RUNNIT_BOARDS_V2_PAGE : ROUTER.RUNNIT_BOARDS_PAGE);
		},
		onTemplatesNavClick () {
			// Always navigate to templates page on click
			this.goTo(ROUTER.RUNNIT_BOARD_TEMPLATES_PAGE);
		},
		toggleSideNavCollapse () {
			const collapsing: boolean = !this.toolbar.runnitsSideNavCollapsed;
			this.updateToolbar({
				runnitsSideNavCollapsed: collapsing,
				userManuallyCollapsedSideNav: collapsing,
			});
		},
		openUserProfile () {
			this.$emit('open-user-profile');
		},
		setCurrentTargetStyle (event: MouseEvent, styleKey: string, value: string) {
			const target = event.currentTarget as HTMLElement | null;
			if (!target) return;
			(target.style as any)[styleKey] = value;
		},
		openSingleToolRunnit (nodeDef: Partial<RunnitNodeDef> & { id: string, title: string }) {
			this.onAddRunnitClick(nodeDef, RUNNIT_TYPE.SINGLE_TOOL);
		},
		// One opener for every Kit. The default tool used to be a synchronous
		// constant; it now comes from the runnitKits binding, so a click before
		// the binding lands has no Kit to open and is ignored rather than
		// routing to a tool that does not exist.
		openKit (kitType) {
			// Last-used tool in this Kit if it is still visible, else the Kit's
			// default. Both checks live in KitsMixin so the nav and the home
			// tiles cannot drift apart.
			const nodeDefId = this.getKitEntryNodeDefId(kitType);
			if (!nodeDefId) return;

			this.onAddRunnitClick({ id: nodeDefId }, RUNNIT_TYPE.SINGLE_TOOL, { kit: kitType });
		},
		containNavWheelEvent (event: WheelEvent) {
			// Always stop propagation to prevent scrolling parent elements
			event.stopPropagation();

			const element: HTMLElement = this.$refs.navContentWrapper as HTMLElement;
			if (!element) {
				event.preventDefault();
				return;
			}

			const { scrollTop, scrollHeight, clientHeight } = element;
			const isAtTop: boolean = scrollTop === 0;
			const isAtBottom: boolean = scrollTop + clientHeight >= scrollHeight - 1;

			// Prevent default only when at boundaries to avoid scrolling beyond or in parent elements
			if ((isAtTop && event.deltaY < 0) || (isAtBottom && event.deltaY > 0)) {
				event.preventDefault();
			}
		},
	},
	destroyed () {
		this.clearDomainClaimFlyoutDismissTimer();
	},
	components: {
		DomainClaimResolutionContent,
		LoadingState,
		ComplexBackgroundSideNav,
		RunnitSideNavAccountCard,
		RunnitAccountSelectionDialog,
		RunnitsLogoMarkSVG,
		RunDiffusionWithRunnitMarkLogo,
		AccountMenuBtn,
		MenuOrBottomSheet,
	},
});
